import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import type { AnyTrackLocation } from "../../../../api/anyTrackApi";
import { useGetAnyTrackLocationsQuery } from "../../../../api/anyTrackApi";
import {
  AssignedTrailerForShare,
  useCreateShareLinkMutation,
  useLazyGetAssignedTrailerQuery,
} from "../../../../api/shareApi";
import type { Vehicle } from "../../../../api/vehiclesApi";
import { useGetVehiclesQuery } from "../../../../api/vehiclesApi";

interface ShareLinkDialogProps {
  open: boolean;
  onClose: () => void;
  initialVehicle?: Vehicle;
  initialTrailer?: AnyTrackLocation;
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
}

function getTrailerDisplayName(trailer: AnyTrackLocation) {
  return trailer.vehicleName?.trim() || trailer.licensePlateNumber || trailer.deviceId;
}

export const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({
  open,
  onClose,
  initialVehicle,
  initialTrailer,
}) => {
  const { data: vehicles = [] } = useGetVehiclesQuery();
  const { data: trailers = [] } = useGetAnyTrackLocationsQuery();
  const [createShareLink, { isLoading: isCreating }] = useCreateShareLinkMutation();
  const [fetchAssignedTrailer] = useLazyGetAssignedTrailerQuery();

  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);
  const [selectedTrailers, setSelectedTrailers] = useState<AnyTrackLocation[]>([]);
  const [assignedTrailers, setAssignedTrailers] = useState<Record<number, AssignedTrailerForShare | null>>({});
  const [includeAssigned, setIncludeAssigned] = useState<Record<number, boolean>>({});
  const [shareName, setShareName] = useState("");
  const [durationDays, setDurationDays] = useState(1);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setSelectedVehicles(initialVehicle ? [initialVehicle] : []);
    setSelectedTrailers(initialTrailer ? [initialTrailer] : []);
    setAssignedTrailers({});
    setIncludeAssigned({});
    setShareName("");
    setDurationDays(1);
    setGeneratedUrl(null);
    setCopySuccess(false);
    setErrorMessage(null);
  }, [initialTrailer, initialVehicle, open]);

  useEffect(() => {
    if (!open) return;

    selectedVehicles.forEach((vehicle) => {
      if (Object.prototype.hasOwnProperty.call(assignedTrailers, vehicle.id)) return;

      fetchAssignedTrailer(vehicle.id)
        .unwrap()
        .then((assignedTrailer) => {
          setAssignedTrailers((current) => ({
            ...current,
            [vehicle.id]: assignedTrailer,
          }));
          if (assignedTrailer.deviceId) {
            setIncludeAssigned((current) => ({
              ...current,
              [vehicle.id]: true,
            }));
          }
        })
        .catch(() => {
          setAssignedTrailers((current) => ({
            ...current,
            [vehicle.id]: null,
          }));
        });
    });
  }, [assignedTrailers, fetchAssignedTrailer, open, selectedVehicles]);

  const includedAssignedTrailers = useMemo(
    () =>
      selectedVehicles
        .map((vehicle) => ({ vehicle, trailer: assignedTrailers[vehicle.id] }))
        .filter(
          (entry): entry is { vehicle: Vehicle; trailer: AssignedTrailerForShare } =>
            Boolean(entry.trailer?.deviceId && includeAssigned[entry.vehicle.id])
        ),
    [assignedTrailers, includeAssigned, selectedVehicles]
  );

  const assetCount = selectedVehicles.length + selectedTrailers.length + includedAssignedTrailers.length;

  const handleGenerate = async () => {
    setErrorMessage(null);

    if (assetCount === 0) {
      setErrorMessage("Please select at least one vehicle or trailer before generating a link.");
      return;
    }

    const trailerDeviceIds = new Set<string>();
    selectedTrailers.forEach((trailer) => trailerDeviceIds.add(trailer.deviceId));
    includedAssignedTrailers.forEach(({ trailer }) => {
      if (trailer.deviceId) trailerDeviceIds.add(trailer.deviceId);
    });

    try {
      const result = await createShareLink({
        name: shareName.trim() || null,
        durationDays,
        vehicleIds: selectedVehicles.map((vehicle) => vehicle.id),
        trailerDeviceIds: Array.from(trailerDeviceIds),
      }).unwrap();

      setGeneratedUrl(result.shareUrl);
    } catch (error: any) {
      console.error("Failed to create share link:", error);
      const apiErrors: string[] | undefined = error?.data?.errors;
      if (apiErrors?.length) {
        setErrorMessage(apiErrors.join(" "));
      } else {
        setErrorMessage("Failed to create share link. Please try again.");
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 2147483647 }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ShareIcon color="primary" />
        Share Live Tracking
        <IconButton onClick={onClose} size="small" aria-label="Close share dialog" sx={{ ml: "auto" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Generate one public link that can track multiple vehicles and trailers until it expires.
          </Typography>

          {generatedUrl ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.25,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Typography
                  component="a"
                  href={generatedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {generatedUrl}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Copy share link"
                  color={copySuccess ? "success" : "default"}
                  onClick={async () => {
                    await copyText(generatedUrl);
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  }}
                >
                  {copySuccess ? <CheckCircleOutlineIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Button variant="outlined" onClick={() => setGeneratedUrl(null)}>
                Generate Another Link
              </Button>
            </Stack>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 180px" },
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <TextField
                  label="Name"
                  value={shareName}
                  onChange={(event) => setShareName(event.target.value)}
                  size="small"
                  fullWidth
                  helperText="Optional. If blank, a name will be generated from the selected assets."
                  placeholder="Example: Customer delivery ETA"
                />

                <TextField
                  label="Duration"
                  type="number"
                  value={durationDays}
                  onChange={(event) => setDurationDays(Math.max(1, Number(event.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  size="small"
                  helperText="Expires after this many days."
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  alignItems: "start",
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Vehicles
                  </Typography>
                  <Autocomplete
                    multiple
                    options={vehicles}
                    value={selectedVehicles}
                    onChange={(_, value) => setSelectedVehicles(value)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    componentsProps={{
                      popper: {
                        sx: { zIndex: 2147483647 },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Choose vehicles" />
                    )}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Additional Trailers
                  </Typography>
                  <Autocomplete
                    multiple
                    options={trailers}
                    value={selectedTrailers}
                    onChange={(_, value) => setSelectedTrailers(value)}
                    getOptionLabel={getTrailerDisplayName}
                    isOptionEqualToValue={(option, value) => option.deviceId === value.deviceId}
                    componentsProps={{
                      popper: {
                        sx: { zIndex: 2147483647 },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Choose trailer GPS devices" />
                    )}
                  />
                </Box>
              </Box>

              {selectedVehicles.length > 0 && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1.4fr 1.6fr 80px", md: "1.5fr 1.75fr 96px" },
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      bgcolor: "action.hover",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Vehicle
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Assigned Trailer
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "center" }}>
                      Include
                    </Typography>
                  </Box>

                  {selectedVehicles.map((vehicle) => {
                    const assignedTrailer = assignedTrailers[vehicle.id];
                    const trailerLabel = assignedTrailer
                      ? assignedTrailer.unitNumber || assignedTrailer.plate || `Trailer ${assignedTrailer.trailerId}`
                      : null;

                    let trailerContent: React.ReactNode;
                    let includeControl: React.ReactNode = (
                      <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center" }}>
                        -
                      </Typography>
                    );

                    if (assignedTrailer === undefined) {
                      trailerContent = (
                        <Typography variant="body2" color="text.secondary">
                          Checking assigned trailer...
                        </Typography>
                      );
                    } else if (assignedTrailer === null) {
                      trailerContent = (
                        <Typography variant="body2" color="text.secondary">
                          No active assigned trailer found
                        </Typography>
                      );
                    } else if (!assignedTrailer.deviceId) {
                      trailerContent = (
                        <Typography variant="body2" color="text.secondary">
                          {trailerLabel} has no AnyTrack device linked
                        </Typography>
                      );
                    } else {
                      trailerContent = (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {trailerLabel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {assignedTrailer.plate || assignedTrailer.deviceId}
                            {assignedTrailer.driverName ? ` • Driver: ${assignedTrailer.driverName}` : ""}
                          </Typography>
                        </Box>
                      );
                      includeControl = (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Checkbox
                            checked={Boolean(includeAssigned[vehicle.id])}
                            onChange={(event) =>
                              setIncludeAssigned((current) => ({
                                ...current,
                                [vehicle.id]: event.target.checked,
                              }))
                            }
                            inputProps={{ "aria-label": `Include assigned trailer for ${vehicle.name}` }}
                          />
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={vehicle.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1.4fr 1.6fr 80px", md: "1.5fr 1.75fr 96px" },
                          gap: 1,
                          alignItems: "center",
                          px: 1.5,
                          py: 1,
                          borderTop: 1,
                          borderColor: "divider",
                          "&:first-of-type": { borderTop: 0 },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={vehicle.name}>
                            {vehicle.name}
                          </Typography>
                          {vehicle.vin && (
                            <Typography variant="caption" color="text.secondary" noWrap title={vehicle.vin}>
                              VIN {vehicle.vin}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>{trailerContent}</Box>
                        {includeControl}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  disabled={isCreating || assetCount === 0}
                  onClick={handleGenerate}
                  startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Generate Link
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
